import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Target, TrendingUp, type LucideIcon } from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

export interface KpiCardProps {
  actual?: number
  goal?: number
  label: string
  icon?: LucideIcon
  isLoading?: boolean
}

// ============================================================================
// KpiCard Component
// ============================================================================

/**
 * KpiCard Component
 *
 * Displays a single KPI metric with Goal and Actual values.
 * Used within KpiGroup to show dashboard metrics.
 *
 * Features:
 * - Goal value with target icon
 * - Actual value with trending icon
 * - Formatted numbers with comma separators (e.g., 1,847)
 * - Color-coded actual value (green if >= goal, amber if < goal)
 * - Loading skeleton state
 * - Shadow effect for depth
 *
 * @param actual - The current actual KPI value
 * @param goal - The target goal value
 * @param label - The KPI label/title
 * @param isLoading - Whether the KPI data is loading
 */
export function KpiCard({ actual, goal, label, icon: Icon, isLoading = false }: KpiCardProps) {
  // Determine if actual meets or exceeds goal
  const meetsGoal = actual !== undefined && goal !== undefined && actual >= goal

  return (
    <Card className="flex flex-col py-6 px-5 shadow-md hover:shadow-lg transition-shadow duration-200">
      {isLoading ? (
        <>
          {/* Loading skeleton for label */}
          <Skeleton className="h-4 w-32 mb-4" />
          {/* Loading skeleton for values */}
          <div className="flex justify-between gap-4">
            <Skeleton className="h-16 w-24" />
            <Skeleton className="h-16 w-24" />
          </div>
          <span className="sr-only">{label}</span>
        </>
      ) : (
        <>
          {/* KPI Label + Icon - Top */}
          <div className="flex items-center gap-2 mb-4">
            {Icon && <Icon className="h-4 w-4 text-[#212d47] shrink-0" />}
            <div className="text-sm font-medium text-gray-600 font-sans">
              {label}
            </div>
          </div>

          {/* Goal and Actual Values Row */}
          <div className="flex justify-between items-end gap-4">
            {/* Goal */}
            <div className="flex flex-col items-center flex-1">
              <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                <Target className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">Goal</span>
              </div>
              <div className="text-3xl font-display font-semibold text-gray-700">
                {goal?.toLocaleString() ?? '—'}
              </div>
            </div>

            {/* Divider */}
            <div className="h-12 w-px bg-gray-200" />

            {/* Actual */}
            <div className="flex flex-col items-center flex-1">
              <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                <TrendingUp className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">Actual</span>
              </div>
              <div className={`text-3xl font-display font-semibold ${meetsGoal ? 'text-green-600' : 'text-amber-600'}`}>
                {actual?.toLocaleString() ?? '—'}
              </div>
            </div>
          </div>
        </>
      )}
    </Card>
  )
}
